import React, { useMemo, useCallback, useState } from "react";
import { useNavigate, generatePath } from "react-router-dom";
import { useListTrapSitesQuery } from "../../actions/trapsiteInQuery";
import { useListDevicesQuery } from "../../actions/devicesInQuery";
import { DetectionStatusData } from "../../types/components/home";
import { DetectionActivity } from "./DetectionActivity";
import { ROUTES } from "../../config/routes";
import "./index.css";
import { useInfiniteAlerts } from "../../hooks/useInfiniteAlerts";

const Index: React.FC = () => {
  const navigate = useNavigate();
  const [invasiveOnly, setInvasiveOnly] = useState(false);
  const { data: devices, isLoading: devicesLoading } = useListDevicesQuery();
  const { data: trapSiteData, isLoading: trapSiteLoading } =
    useListTrapSitesQuery();

  const {
    allAlerts,
    isLoading: alertsLoading,
    isFetchingMore,
    handleScroll,
  } = useInfiniteAlerts({
    types: ["detection"],
    msg_types: invasiveOnly
      ? ["invasive_species_detected"]
      : ["invasive_species_detected", "detection_created"],
    pollingInterval: 60000,
  });

  const handleClickAlert = useCallback(
    (id: string) => {
      const alert = allAlerts.find((a) => a._id === id);
      if (alert?.context?.data?.detection_id) {
        navigate(
          generatePath(ROUTES.specimen_detail, {
            id: alert.context.data.detection_id,
          }),
        );
      } else {
        console.warn(
          `No detection_id found in alert context for alert id: ${id}`,
        );
      }
    },
    [allAlerts, navigate],
  );

  const joinedAlerts = useMemo(() => {
    if (!allAlerts.length) {
      return [];
    }
    let result: DetectionStatusData[] = [];

    allAlerts.forEach((alert) => {
      if (alert.type !== "detection") {
        return;
      }
      const device = devices?.items?.find(
        (d) => d.uuid === alert.context.device,
      );
      const trapSite = trapSiteData?.items?.find((t) => {
        return t._id === (alert.context?.trap_site_id || device?.trap_site_id);
      });
      if (!alert?.skip) {
        result.push({
          _id: alert._id,
          message: alert.message
            ? alert.message
            : alert.msg_type === "invasive_species_detected"
              ? "Invasive mosquito detected"
              : "Mosquito detected",
          device_name: device?.display_name || "",
          trapsite_name: trapSite?.display_name,
          created_at: alert.created_at,
          isInvasive: alert.msg_type === "invasive_species_detected",
        });
      }
    });

    return result;
  }, [allAlerts, devices, trapSiteData]);

  return (
    <DetectionActivity
      loading={trapSiteLoading || devicesLoading || alertsLoading}
      isFetchingMore={isFetchingMore}
      joinedAlerts={joinedAlerts}
      invasiveOnly={invasiveOnly}
      setInvasiveOnly={setInvasiveOnly}
      onScroll={handleScroll}
      onAlertClick={handleClickAlert}
    />
  );
};

export default Index;
